import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, CssBaseline, GlobalStyles, IconButton, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginScreen from './screens/LoginScreen';
import HomeScreen from './screens/HomeScreen';
import MembersScreen from './screens/MembersScreen';
import OrdersScreen from './screens/OrdersScreen';
import MessagesScreen from './screens/MessagesScreen';
import ExamDatesScreen from './screens/ExamDatesScreen';
import {
  clearGabbaiSession,
  fetchGabbaiMe,
  fetchGabbaiMeta,
  getGabbaiToken,
} from './gabbaiPortalApi';
import { getGabbaiTheme } from './gabbaiPortalTheme';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@500;700&family=Assistant:wght@400;600;700&display=swap';

const ENV_EXAM_MODE = process.env.REACT_APP_EXAM_MODE || 'predefined';
const ENV_SYSTEM_LABEL =
  process.env.REACT_APP_SYSTEM_LABEL ||
  (ENV_EXAM_MODE === 'open-gemara' ? 'מרא דתלמודא' : 'מרא דהלכתא');
const IS_OPEN_GEMARA = ENV_EXAM_MODE === 'open-gemara';

const SCREEN_TITLES = {
  home: 'אזור אישי',
  members: 'רשימת החברים',
  orders: 'הזמנות מבחנים',
  messages: 'הודעות מהמערכת',
  dates: 'זמני מבחנים',
};

export default function GabbaiPortal() {
  const theme = getGabbaiTheme();
  const [booting, setBooting] = useState(true);
  const [session, setSession] = useState(null);
  const [meta, setMeta] = useState(null);
  const [screen, setScreen] = useState('home');
  const [activeKolelId, setActiveKolelId] = useState('');
  const [bootError, setBootError] = useState('');

  useEffect(() => {
    if (document.getElementById('gabbai-portal-fonts')) return;
    const link = document.createElement('link');
    link.id = 'gabbai-portal-fonts';
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  const logout = useCallback(() => {
    clearGabbaiSession();
    setSession(null);
    setMeta(null);
    setScreen('home');
    setActiveKolelId('');
  }, []);

  const loadSession = useCallback(async () => {
    const token = getGabbaiToken();
    if (!token) {
      setBooting(false);
      return;
    }
    try {
      const [me, metaData] = await Promise.all([fetchGabbaiMe(), fetchGabbaiMeta()]);
      setSession(me);
      setMeta(metaData);
      const kolels = me.kolels || [];
      setActiveKolelId((prev) => prev || kolels[0]?.id || '');
      setBootError('');
    } catch (err) {
      clearGabbaiSession();
      setSession(null);
      setMeta(null);
      if (err.response?.status !== 401) {
        setBootError(err.response?.data?.error || 'שגיאה בטעינת הפורטל');
      }
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    if (IS_OPEN_GEMARA) {
      setBooting(false);
      return;
    }
    loadSession();
  }, [loadSession]);

  const handleLoginSuccess = async (data) => {
    setSession({
      phone: data.phone,
      gabaiName: data.gabaiName,
      isAdmin: data.isAdmin,
      kolels: data.kolels || [],
    });
    setActiveKolelId(data.kolels?.[0]?.id || '');
    setScreen('home');
    try {
      const metaData = await fetchGabbaiMeta();
      setMeta(metaData);
    } catch {
      setMeta(null);
    }
  };

  const systemLabel = meta?.systemLabel || ENV_SYSTEM_LABEL;
  const kolels = session?.kolels || meta?.kolels || [];

  return (
    <>
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: { background: theme.htmlBg, overflowX: 'hidden' },
          body: {
            margin: 0,
            background: theme.htmlBg,
            overflowX: 'hidden',
            WebkitTextSizeAdjust: '100%',
          },
          '#root': { overflowX: 'hidden', maxWidth: '100vw' },
        }}
      />
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '100dvh', sm: '100vh' },
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          direction: 'rtl',
          fontFamily: '"Assistant", "Rubik", sans-serif',
          background: theme.pageBg,
          pb: { xs: 'max(24px, env(safe-area-inset-bottom))', sm: 6 },
          pt: 'env(safe-area-inset-top)',
          ...(theme.gridBg && {
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: theme.gridBg,
              backgroundSize: theme.gridSize || '160px 160px',
              backgroundPosition: 'center',
              backgroundRepeat: 'repeat',
              opacity: theme.gridOpacity ?? 0.12,
              pointerEvents: 'none',
              zIndex: 0,
              maskImage: 'radial-gradient(ellipse at 50% 30%, black 0%, transparent 78%)',
              WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 0%, transparent 78%)',
            },
          }),
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: { xs: 2, sm: 3 },
              py: 1.5,
              minHeight: 56,
            }}
          >
            {session && screen !== 'home' ? (
              <IconButton
                onClick={() => setScreen('home')}
                sx={{ color: 'rgba(245,240,230,0.8)' }}
                aria-label="חזרה"
              >
                <ArrowForwardIcon />
              </IconButton>
            ) : (
              <Box sx={{ width: 40 }} />
            )}
            {session ? (
              <Button
                startIcon={<LogoutIcon />}
                onClick={logout}
                sx={{ color: 'rgba(245,240,230,0.75)', fontSize: '0.85rem' }}
              >
                יציאה
              </Button>
            ) : (
              <Box sx={{ width: 40 }} />
            )}
          </Box>

          <Box sx={{ textAlign: 'center', px: 2, mb: 3 }}>
            <Box
              component="img"
              src={theme.logo}
              alt={systemLabel}
              sx={{
                width: theme.logoWidth,
                maxWidth: 'min(92vw, 320px)',
                height: 'auto',
                mb: 0.5,
                filter: 'drop-shadow(0 10px 28px rgba(0,0,0,0.45))',
              }}
            />
            <Typography sx={{ color: 'rgba(245,240,230,0.78)', mt: 0.5, fontWeight: 600 }}>
              {session ? (SCREEN_TITLES[screen] || 'פורטל גבאים') : 'פורטל גבאים'}
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
            {IS_OPEN_GEMARA && (
              <Typography sx={{ color: '#f5f0e6', textAlign: 'center', mt: 4 }}>
                פורטל הגבאים אינו זמין במערכת זו.
              </Typography>
            )}

            {!IS_OPEN_GEMARA && booting && (
              <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: theme.accent }} />
              </Box>
            )}

            {!IS_OPEN_GEMARA && !booting && bootError && !session && (
              <Typography sx={{ color: '#f5a5a5', textAlign: 'center', mb: 2 }}>
                {bootError}
              </Typography>
            )}

            {!IS_OPEN_GEMARA && !booting && !session && (
              <LoginScreen onSuccess={handleLoginSuccess} />
            )}

            {!IS_OPEN_GEMARA && !booting && session && screen === 'home' && (
              <HomeScreen
                gabaiName={session.gabaiName || meta?.gabaiName}
                kolels={kolels}
                activeKolelId={activeKolelId}
                onKolelChange={setActiveKolelId}
                messagesCount={meta?.messagesCount || 0}
                onNavigate={setScreen}
              />
            )}

            {!IS_OPEN_GEMARA && !booting && session && screen === 'members' && (
              <MembersScreen
                activeKolelId={activeKolelId}
                meta={meta}
                onBack={() => setScreen('home')}
              />
            )}

            {!IS_OPEN_GEMARA && !booting && session && screen === 'orders' && (
              <OrdersScreen
                activeKolelId={activeKolelId}
                kolels={kolels}
                meta={meta}
              />
            )}

            {!IS_OPEN_GEMARA && !booting && session && screen === 'messages' && (
              <MessagesScreen />
            )}

            {!IS_OPEN_GEMARA && !booting && session && screen === 'dates' && (
              <ExamDatesScreen />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}
